<script setup lang="ts">
import { IconError, IconFile } from '#shared'
import { computed } from 'vue'
import type { FileAttachment } from '#entities/message'
import AttachmentLoader from './attachment-loader.vue'
import AttachmentRemoveButton from './attachment-remove-button.vue'
import AttachmentReuploadButton from './attachment-reupload-button.vue'

const { attachment } = defineProps<{
  attachment: Omit<FileAttachment, 'id'>
}>()

const uploadState = computed(() => attachment.meta?.uploadState)

const emit = defineEmits<{ delete: []; reupload: [] }>()

const onDelete = () => emit('delete')
const onReupload = () => emit('reupload')
</script>

<template>
  <li :class="$style.root">
    <IconFile />

    <div :class="$style.main">
      <div :class="$style.fileName">{{ attachment.name }}</div>
      <div v-if="uploadState === 'errored'" :class="$style.error">
        <IconError />
        <div :class="$style.errorText">Failed to upload</div>
      </div>
    </div>

    <AttachmentLoader v-if="uploadState === 'pending'" :class="$style.loader" />

    <AttachmentReuploadButton v-if="uploadState === 'errored'" :class="$style.reupload" @click="onReupload" />
    <AttachmentRemoveButton :class="$style.remove" @click="onDelete" />
  </li>
</template>

<style lang="scss" module>
.root {
  display: flex;
  align-items: center;
  gap: 10px;

  &:not(:hover) {
    .remove {
      opacity: 0;
    }

    .reupload {
      opacity: 0;
    }
  }
}

.loader {
  width: 80px;
}

.main {
  min-width: 0;
  flex-grow: 1;
  display: flex;
  align-items: center;
  gap: 10px;
}

.fileName {
  min-width: 0;
  max-width: fit-content;
  flex-grow: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.error {
  flex-shrink: 0;
  display: flex;
  gap: 5px;
}

.errorText {
  color: #f44336;
  font-size: 14px;
}
</style>
